import React from 'react';

import { SelectTextField } from '@lumapps/combobox/components/SelectTextField';
import { getCurrentInstance } from '@lumapps/instance/ducks/selectors';
import { useQueryInstanceSiblings } from '@lumapps/instance/hooks/useQueryInstanceSiblings';
import { INSTANCE } from '@lumapps/instance/keys';
import { Instance } from '@lumapps/instance/types';
import { Filter } from '@lumapps/lumx-filter-and-sort/components/Filter';
import { MultipleSelectFilterProps } from '@lumapps/lumx-filter-and-sort/components/MultipleSelectFilter';
import { useFilterUIProps } from '@lumapps/lumx-filter-and-sort/hooks/useFilterUIProps';
import { useListFilter, UseListFilterOptions } from '@lumapps/lumx-filter-and-sort/hooks/useListFilter';
import { useSelector } from '@lumapps/redux/react';
import { useTranslate } from '@lumapps/translations';
import { PartialExcept } from '@lumapps/utils/types/PartialBy';

type PartialInstance = PartialExcept<Instance, 'id' | 'name'>;

interface InstanceSiblingsMultipleFilterProps
    extends Omit<
        MultipleSelectFilterProps<Instance>,
        'options' | 'onClearSelected' | 'selectTextFieldProps' | 'title' | 'defaults'
    > {
    /** custom select text field props */
    selectTextFieldProps?: MultipleSelectFilterProps<Instance>['selectTextFieldProps'];
    /** custom title */
    title?: string;
    /** default values for the filter */
    defaults?: Partial<UseListFilterOptions<PartialInstance>>;
}

const CLASSNAME = 'instance-siblings-multiple-filter';

export const InstanceSiblingsMultipleFilter: React.FC<InstanceSiblingsMultipleFilterProps> = ({
    selectTextFieldProps,
    onSelectedMultiple,
    selection = [],
    onChange,
    mode,
    ...props
}) => {
    const { translateKey } = useTranslate();
    const currentInstance = useSelector(getCurrentInstance);

    const title = props.title || translateKey(INSTANCE.INSTANCES_TITLE);

    const filterUIProps = useFilterUIProps({
        parentClassName: CLASSNAME,
        className: selectTextFieldProps?.className,
    });

    const [enabled, setEnabled] = React.useState(false);
    const { baseLoadingStatus, data: sites = [] } = useQueryInstanceSiblings(
        {
            instanceId: currentInstance.id,
        },
        { enabled },
    );

    return (
        <Filter title={title} {...props} hideTitle mode={mode}>
            <SelectTextField<Instance>
                selectionType="multiple"
                autoFilter={false}
                getOptionId="id"
                getOptionName="name"
                label={title}
                labelProps={{
                    ...selectTextFieldProps?.labelProps,
                    children: title,
                    typography: filterUIProps.typography,
                }}
                className={filterUIProps.className}
                onChange={(instances = []) => {
                    onChange?.(instances);
                    onSelectedMultiple?.(instances);
                }}
                isDisabled={selectTextFieldProps?.isDisabled}
                value={selection}
                options={sites}
                status={baseLoadingStatus}
                onFocus={() => setEnabled(true)}
            />
        </Filter>
    );
};

Object.assign(InstanceSiblingsMultipleFilter, { hook: useListFilter });
